/**
 * BrandHead (TMMAPP-0035) — keeps the document head in step with the active brand theme: the
 * favicon (`favicon.svg`, else `favicon.png`; `index.html`'s static `/favicon.svg` is the pre-JS
 * default), an `apple-touch-icon` only when the brand has one, the fallback `document.title` =
 * `productName` (each page sets "Page · Product" itself with the same name, TMMAPP-0072),
 * `<meta name="theme-color">` = the active mode's `appBar`, and one
 * `<link rel="stylesheet">` per distinct Google Fonts URL (removed when the theme changes; the
 * URLs carry `display=swap`, so rendering never blocks on a font). A face whose family is Inter
 * or JetBrains Mono has no link: it renders from the bundled `InterVariable` /
 * `JetBrains Mono Variable` (TMMAPP-0084 / 0085, `brandFontUrls`).
 * Renders nothing.
 */
import { useEffect } from 'react';
import { useBrandTheme } from '@/context/brand-theme-context';
import { setFallbackDocumentTitle } from '@/shared/useDocumentTitle';
import { brandFontUrls } from './brandTheme';
import type { ThemeMode } from './tokens';
import { useBrand } from './useBrand';

const FONT_LINK_ATTR = 'data-brand-font';

function headLink(rel: string): HTMLLinkElement | null {
  return document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
}

export function BrandHead({ mode }: { mode: ThemeMode }) {
  const { brandTheme: brand } = useBrandTheme();
  const { assets, productName } = useBrand();
  const appBar = brand.modes[mode].appBar;
  const fontUrls = brandFontUrls(brand).join('\n');

  useEffect(() => {
    setFallbackDocumentTitle(productName);
  }, [productName]);

  useEffect(() => {
    for (const meta of document.head.querySelectorAll<HTMLMetaElement>(
      'meta[name="theme-color"]',
    )) {
      meta.content = appBar;
    }
  }, [appBar]);

  const { svg, png } = assets.favicon;
  useEffect(() => {
    const href = svg ?? png;
    if (href === null) return;
    let link = headLink('icon');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = svg !== null ? 'image/svg+xml' : 'image/png';
    link.href = href;
  }, [svg, png]);

  const touch = assets.appleTouchIcon;
  useEffect(() => {
    const existing = headLink('apple-touch-icon');
    if (touch === null) {
      existing?.remove();
      return;
    }
    const link = existing ?? document.createElement('link');
    link.rel = 'apple-touch-icon';
    link.href = touch;
    if (!existing) document.head.appendChild(link);
  }, [touch]);

  useEffect(() => {
    if (fontUrls === '') return undefined;
    const links = fontUrls.split('\n').map((href) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute(FONT_LINK_ATTR, '');
      document.head.appendChild(link);
      return link;
    });
    return () => {
      for (const link of links) link.remove();
    };
  }, [fontUrls]);

  return null;
}
